import React, { useState, useEffect } from 'react';
import './Quickbooks.css';
import qbLogo from '../../../assets/images/integrations/quickbooks.png';
import qbDefault from '../../../assets/images/integrations/quickbooks/qb_connect_default.png';
import qbHover from '../../../assets/images/integrations/quickbooks/qb_connect_hover.png';
import {ReactComponent as SuccessIcon} from '../../../assets/icons/checkmark-icon.svg';
import qb_vairo from '../../../assets/images/integrations/quickbooks/qb_vairo.png';
import Popup from '../../../components/Popup/Popup';
import LoadingScreen from '../../../components/LoadingScreen/LoadingScreen';

function Quickbooks({ dataSourceId, authStatus, setNewDataSource, setAuthStatus, setAddDataSourceOpen, connectDataSource, disconnectDataSource }) {
    const [isHovering, setIsHovering] = useState(false);
    const [termsOpen, setTermsOpen] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const imagesToLoad = [qbLogo, qbDefault, qbHover, qb_vairo];
        let loadedImages = 0;

        const preloadImage = (src) => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.src = src;
                img.onload = resolve;
                img.onerror = reject;
            });
        };

        Promise.all(imagesToLoad.map(preloadImage))
            .then(() => setImagesLoaded(true))
            .catch(err => console.error('Error preloading images:', err));
    }, []);

    useEffect(() => {
        const handleMessage = (event) => {
            // Allow messages from both your API and Intuit's domain
            const allowedOrigins = [
                process.env.REACT_APP_API_BASE_URL,
                'https://oauth.platform.intuit.com'  // Add Intuit's OAuth domain
            ];
            
            if (!allowedOrigins.includes(event.origin)) {
                console.log('Rejected message from:', event.origin);
                return;
            }
            
            if (event.data.status === 'success') {
                console.log('Authorization successful.');
                setAuthStatus('success');
                setNewDataSource((prevDataSource) => ({
                    ...prevDataSource,
                    status: 'not-synced'
                }));
            } else {
                console.log('Authorization failed.');
                setAuthStatus('failure');
                setErrorMessage("Authorization Failed. Please try again.");
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [setAuthStatus]);

    const handleLinkAccount = (e) => {
        setErrorMessage("");
        if (!termsAccepted) {
            setErrorMessage("Please read and agree to the terms of service to continue.");
            return;
        }
        const width = (window.screen.width * 0.5);
        const height = (window.screen.height * 0.8);
        const left = (window.screen.width * 0.5 / 2);
        const top = (window.screen.height * 0.8 / 2);

        const popup = window.open(
            `${process.env.REACT_APP_API_BASE_URL}/api/integrations/quickbooks/auth/${dataSourceId}`,
            'QuickBooksAuth',
            `width=${width},height=${height},top=${top},left=${left}`
        );
    };

    const handleTermsClick = (e) => {
        e.preventDefault();
        setTermsOpen(true);
    };

    const renderTerms = () => {
        return (
            <Popup
                content={
                    <div className="terms-modal">
                        <h2>Terms of Service</h2>
                        <p>
                            By integrating Vairo with QuickBooks, you enable Vairo to securely access your financial data to provide AI-driven insights, automated reporting, and real-time analytics tailored to your business needs. This integration allows Vairo to analyze transaction data, expenses, revenue trends, and other financial metrics to help you make informed decisions effortlessly.
                        </p>
                        <p>
                            We value your privacy and security. Your data will only be used for the purposes of generating insights, visualizations, and reports within Vairo. We do not share, sell, or use your data for any other purposes.
                        </p>
                        <p>
                            By proceeding with this integration, you acknowledge and agree that Vairo will access and process your QuickBooks data solely for these purposes.
                        </p>
                        <div className="terms-acceptance">
                            <label className="checkbox-container">
                                <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />
                                <span>I have read and agree to the terms of service</span>
                            </label>
                            <button className="continue-button" disabled={!termsAccepted} onClick={() => {
                                setTermsOpen(false);
                            }}
                            >Continue</button>
                        </div>
                    </div>
                }
                onClose={() => setTermsOpen(false)}
            />
        );
    };

    return (
        <div className="link-source quickbooks">
            {imagesLoaded ? (
                <>
                    {termsOpen && renderTerms()}
                    <div className="logo-connection">
                        <img src={qb_vairo} className="qb-vairo-logo" alt="Quickbooks Vairo Logo" />
                    </div>
                    {(!authStatus || authStatus === 'failure') &&
                        <div className="header">
                            <h2>Log in to QuickBooks to<br />connect to Vairo</h2>
                            <h3>You must have full admin permissions to your QuickBooks account to connect and have read & agreed to the terms.</h3>
                            <label className="checkbox-container">
                                <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />
                                <span>I have read and agree to the <a className="terms" onClick={handleTermsClick}><u>terms of service</u></a></span>
                            </label>
                        </div>
                    }
                    
                    {authStatus === 'success' ? (
                        <div className="success-message">
                            <h2>Success!</h2>
                            <h4>Your QuickBooks account has been linked.<br />Please complete the setup process to begin analyzing.</h4>
                            <h5 onClick={disconnectDataSource}>Disconnect</h5>

                        </div>
                    // ) : authStatus === 'failure' ? (
                    //     <>
                    //         <div className="link-button" onClick={handleLinkAccount}>
                    //             <img src={qbLogo} alt="Quickbooks Logo" />
                    //             <h4>Link Account</h4>
                    //         </div>
                    //         <div className="failure-message">
                    //             <h4>Authorization Failed. Please try again.</h4>
                    //         </div>
                    //     </>
                    ) : (
                        <div className="link-button" onClick={handleLinkAccount} onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
                            <img src={isHovering ? qbHover : qbDefault} alt="Quickbooks Logo" />
                        </div>
                    )}
                    <div className={`add-item-actions ${authStatus === 'success' ? '' : 'disabled'}`}>
                        <button className="add-item-button cancel" onClick={() => setAddDataSourceOpen(false)}>Later</button>
                        <button className={`add-item-button create ${(authStatus !== 'success' ? 'disabled' : '')}`} disabled={authStatus !== 'success'} onClick={() => connectDataSource()}>Begin Setup</button>
                    </div>
                    {errorMessage && <div className="error-message">{errorMessage}</div>}
                </>
            ) : (
                <LoadingScreen />
            )}
        </div>
    );
}

export default Quickbooks;